// ---------------------------------------------------------------------------
// Prezio Onboard Widget — API Client
// ---------------------------------------------------------------------------
// Typed fetch wrapper. All API calls go through this class.
// Token is kept in memory only — never localStorage, never logged.
// ---------------------------------------------------------------------------

import { ApiError } from '@/api/types';
import type { OnboardSession, StepId, StepOptionsResponse, ValidationResult } from '@/api/types';
import {
  sessionUrl,
  optionsUrl,
  validateUrl,
  completeUrl,
  cancelUrl,
  backUrl,
  navigateUrl,
} from '@/api/endpoints';
import { getChosenLocale, isSupportedLocale, toApiLanguage } from '@/i18n';
import type { Locale } from '@/types/config';

/** Default request timeout in milliseconds. */
const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * `Accept-Language` for the backend, or nothing. Sent only for a locale the
 * host or resolver chose and the widget supports: otherwise the browser's own
 * header decides, and an arbitrary value could fail the CORS preflight.
 */
function languageHeader(): Record<string, string> {
  const locale = getChosenLocale();
  if (!locale || !isSupportedLocale(locale)) return {};
  return { 'Accept-Language': toApiLanguage(locale) };
}

interface ApiClientConfig {
  baseUrl: string;
  sessionId: string;
  token: string;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly sessionId: string;
  private readonly token: string;

  constructor({ baseUrl, sessionId, token }: ApiClientConfig) {
    this.baseUrl = baseUrl;
    this.sessionId = sessionId;
    this.token = token;
  }

  // -------------------------------------------------------------------------
  // Core request helper
  // -------------------------------------------------------------------------

  private async request<T>(url: string, options: RequestInit = {}): Promise<T> {
    const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);

    try {
      const response = await fetch(fullUrl, {
        ...options,
        credentials: 'omit',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Token ${this.token}`,
          ...languageHeader(),
          ...(options.headers as Record<string, string> | undefined),
        },
      });

      if (!response.ok) {
        const body: unknown = await response.json().catch(() => ({}));
        throw new ApiError(response.status, body);
      }

      return (await response.json()) as T;
    } catch (error) {
      if (error instanceof ApiError) throw error;

      // AbortController timeout
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new ApiError(0, { code: 'timeout', detail: 'Request timed out' });
      }

      // Network failure
      throw new ApiError(0, { code: 'network_error', detail: 'Network request failed' });
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // -------------------------------------------------------------------------
  // Public methods
  // -------------------------------------------------------------------------

  /** Fetch the current session state. */
  getSession(): Promise<OnboardSession> {
    return this.request<OnboardSession>(sessionUrl(this.baseUrl, this.sessionId));
  }

  /** Fetch options for a step. Uses the absolute URL provided by the API. */
  getOptions(absoluteUrl: string): Promise<StepOptionsResponse> {
    return this.request<StepOptionsResponse>(absoluteUrl);
  }

  /** Fetch options for a step by constructing the URL from step ID. */
  getStepOptions(step: StepId): Promise<StepOptionsResponse> {
    return this.request<StepOptionsResponse>(optionsUrl(this.baseUrl, this.sessionId, step));
  }

  /** Submit step data to advance the flow. */
  submitStep(step: StepId, data: Record<string, unknown>): Promise<OnboardSession> {
    return this.request<OnboardSession>(sessionUrl(this.baseUrl, this.sessionId), {
      method: 'PATCH',
      body: JSON.stringify({ step, data }),
    });
  }

  /** Save the session language without submitting a step. */
  updateLocale(locale: Locale): Promise<OnboardSession> {
    return this.request<OnboardSession>(sessionUrl(this.baseUrl, this.sessionId), {
      method: 'PATCH',
      body: JSON.stringify({ locale: toApiLanguage(locale) }),
    });
  }

  /** Validate step data without persisting (for on-blur validation). */
  validateStep(step: StepId, data: Record<string, unknown>): Promise<ValidationResult> {
    return this.request<ValidationResult>(validateUrl(this.baseUrl, this.sessionId), {
      method: 'POST',
      body: JSON.stringify({ step, data }),
    });
  }

  /** Apply and complete the session. Idempotent — safe to call twice. */
  completeSession(): Promise<OnboardSession> {
    return this.request<OnboardSession>(completeUrl(this.baseUrl, this.sessionId), {
      method: 'POST',
      body: JSON.stringify({ confirm: true }),
    });
  }

  /** Cancel the session with an optional reason. */
  cancelSession(reason?: string): Promise<OnboardSession> {
    return this.request<OnboardSession>(cancelUrl(this.baseUrl, this.sessionId), {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  /** Go back to the previous step. */
  goBack(): Promise<OnboardSession> {
    return this.request<OnboardSession>(backUrl(this.baseUrl, this.sessionId), {
      method: 'POST',
    });
  }

  /** Navigate to a previously completed step (jump-to-edit from summary). */
  navigateToStep(step: string): Promise<OnboardSession> {
    return this.request<OnboardSession>(navigateUrl(this.baseUrl, this.sessionId), {
      method: 'POST',
      body: JSON.stringify({ step }),
    });
  }
}
